import { formatVisitDateLabel } from "./circumference-utils";
import {
  buildLinearXAxis,
  createAreaGradient,
  resolveXAxisBounds,
  resolveYAxisBounds,
  timelineToPoints,
} from "./chart-axis-utils";
import { resolveSparklineColor } from "./MuscleEvolutionSparkline";

export const buildMuscleRegionEvolutionChartModel = ({
  timeline = [],
  unit = "cm",
  trend = "flat",
  progressTone = "medium",
  regionLabel = "",
}) => {
  const points = timelineToPoints(timeline);

  if (points.length === 0) {
    return {
      chartData: { datasets: [] },
      chartOptions: {},
      hasValues: false,
    };
  }

  const borderColor = resolveSparklineColor({ trend, progressTone });
  const { xMin, xMax, spanDays, xTicks, monthStarts } = resolveXAxisBounds(points);
  const yValues = points.map((point) => point.y);
  const { suggestedMin, suggestedMax } = resolveYAxisBounds(yValues);

  const chartData = {
    datasets: [
      {
        label: regionLabel,
        unit,
        parsing: false,
        data: points.map((point) => ({ x: point.x, y: point.y })),
        borderColor,
        backgroundColor: (context) => createAreaGradient(context.chart, borderColor),
        fill: "origin",
        borderWidth: 3,
        borderCapStyle: "round",
        borderJoinStyle: "round",
        cubicInterpolationMode: "monotone",
        tension: 0.35,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: "#FFFFFF",
        pointBorderColor: borderColor,
        pointBorderWidth: 2,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        left: 0,
        right: 8,
        top: 8,
        bottom: 0,
      },
    },
    interaction: {
      mode: "nearest",
      axis: "x",
      intersect: false,
    },
    scales: {
      x: buildLinearXAxis({ points, xMin, xMax, spanDays, xTicks, monthStarts }),
      y: {
        beginAtZero: false,
        suggestedMin,
        suggestedMax,
        border: { display: false },
        grid: {
          color: "#EEF0F3",
          lineWidth: 1,
          drawBorder: false,
        },
        ticks: {
          color: "#9CA3AF",
          font: { size: 12, weight: "500" },
          padding: 10,
          callback: (value) =>
            `${Number(value).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} ${unit}`,
        },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#1F1F23",
        titleColor: "#FFFFFF",
        bodyColor: "#FFFFFF",
        padding: 12,
        cornerRadius: 10,
        displayColors: false,
        callbacks: {
          title: (items) => {
            const index = items?.[0]?.dataIndex;
            const visitDate = points[index]?.visitDate;
            return visitDate ? formatVisitDateLabel(visitDate) : "";
          },
          label: (context) => {
            const value = context.parsed.y;
            if (value == null) {
              return "—";
            }
            return `${Number(value).toLocaleString("pt-BR", {
              maximumFractionDigits: 1,
            })} ${unit}`;
          },
        },
      },
    },
  };

  return {
    chartData,
    chartOptions,
    hasValues: true,
  };
};
